import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const STATUS_CONFIG = {
  success: {
    label: 'Başarılı',
    icon: 'CheckCircle',
    className: 'bg-success/10 text-success border-success/20',
    iconClass: 'text-success',
  },
  error: {
    label: 'Hata',
    icon: 'XCircle',
    className: 'bg-error/10 text-error border-error/20',
    iconClass: 'text-error',
  },
  skipped: {
    label: 'Atlandı',
    icon: 'MinusCircle',
    className: 'bg-warning/10 text-warning border-warning/20',
    iconClass: 'text-warning',
  },
};

const SOURCE_LABELS = {
  store: 'Mağaza',
  event: 'Etkinlik',
  manual: 'Manuel',
};

const MysoftSyncLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [resendResult, setResendResult] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let query = supabase?.from('invoice_logs')?.select('*')?.order('created_at', { ascending: false })?.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (statusFilter !== 'all') {
        query = query?.eq('status', statusFilter);
      }
      if (sourceFilter !== 'all') {
        query = query?.eq('source', sourceFilter);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setLogs(data || []);
    } catch (err) {
      console.error('Mysoft sync logs fetch error:', err);
      setError(err?.message || 'Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleResend = async (log) => {
    setResendingId(log?.id);
    setResendResult(null);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) throw new Error('Oturum bulunamadı');

      // Fetch the order to get full data for resend
      let orderData = null;
      if (log?.order_id) {
        const { data: order } = await supabase?.from('orders')?.select('*')?.eq('id', log?.order_id)?.single();
        orderData = order;
      }

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-mysoft-invoice`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderId: log?.order_id,
            orderNumber: log?.order_number,
            isRetry: true,
            retryOf: log?.id,
            // Pass customer info from the log if available
            customerEmail: log?.customer_email || orderData?.customer_email,
            customerTc: log?.customer_vkn_tckn,
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result?.error || result?.message || 'Yeniden gönderme başarısız');
      }

      setResendResult({ success: true, message: 'Fatura başarıyla yeniden gönderildi!', logId: log?.id });
      // Refresh logs after resend
      setTimeout(() => {
        fetchLogs();
        setResendResult(null);
      }, 3000);
    } catch (err) {
      console.error('Resend error:', err);
      setResendResult({ success: false, message: err?.message || 'Yeniden gönderme başarısız', logId: log?.id });
    } finally {
      setResendingId(null);
    }
  };

  const filteredLogs = logs?.filter(log => {
    if (!searchTerm) return true;
    const term = searchTerm?.toLowerCase();
    return (log?.order_number?.toLowerCase()?.includes(term) ||
    log?.customer_name?.toLowerCase()?.includes(term) ||
    log?.customer_vkn_tckn?.toLowerCase()?.includes(term) ||
    log?.customer_email?.toLowerCase()?.includes(term) || log?.doc_no?.toLowerCase()?.includes(term));
  });

  const stats = {
    total: logs?.length,
    success: logs?.filter(l => l?.status === 'success')?.length,
    error: logs?.filter(l => l?.status === 'error')?.length,
    skipped: logs?.filter(l => l?.status === 'skipped')?.length,
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts)?.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="RefreshCw" size={22} className="text-primary" />
            Mysoft Senkronizasyon Logları
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Mysoft'a gönderilen fatura işlemlerinin durumu ve geçmişi
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={fetchLogs}
          disabled={loading}
        >
          Yenile
        </Button>
      </div>
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Toplam', value: stats?.total, icon: 'List', color: 'text-foreground', bg: 'bg-muted/50' },
          { label: 'Başarılı', value: stats?.success, icon: 'CheckCircle', color: 'text-success', bg: 'bg-success/10' },
          { label: 'Hatalı', value: stats?.error, icon: 'XCircle', color: 'text-error', bg: 'bg-error/10' },
          { label: 'Atlandı', value: stats?.skipped, icon: 'MinusCircle', color: 'text-warning', bg: 'bg-warning/10' },
        ]?.map(stat => (
          <div key={stat?.label} className={`${stat?.bg} rounded-lg p-3 flex items-center gap-3`}>
            <Icon name={stat?.icon} size={20} className={stat?.color} />
            <div>
              <p className={`text-lg font-bold ${stat?.color}`}>{stat?.value}</p>
              <p className="text-xs text-muted-foreground">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Sipariş no, müşteri adı, TC/VKN ara..."
              value={searchTerm}
              onChange={e => setSearchTerm(e?.target?.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e?.target?.value); setPage(0); }}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="success">Başarılı</option>
            <option value="error">Hatalı</option>
            <option value="skipped">Atlandı</option>
          </select>
          <select
            value={sourceFilter}
            onChange={e => { setSourceFilter(e?.target?.value); setPage(0); }}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
          >
            <option value="all">Tüm Kaynaklar</option>
            <option value="store">Mağaza</option>
            <option value="event">Etkinlik</option>
            <option value="manual">Manuel</option>
          </select>
        </div>
      </div>
      {/* Resend Result Banner */}
      {resendResult && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${
          resendResult?.success
            ? 'bg-success/10 border-success/20 text-success' :'bg-error/10 border-error/20 text-error'
        }`}>
          <Icon name={resendResult?.success ? 'CheckCircle' : 'XCircle'} size={18} />
          <span className="text-sm font-medium">{resendResult?.message}</span>
        </div>
      )}
      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-error/10 border border-error/20 text-error">
          <Icon name="AlertCircle" size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {/* Logs Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : filteredLogs?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Icon name="FileX" size={40} className="text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground font-medium">Log bulunamadı</p>
            <p className="text-sm text-muted-foreground/60 mt-1">Filtrelerinizi değiştirmeyi deneyin</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Durum</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sipariş No</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Müşteri</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tutar</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Kaynak</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tarih</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Neden / Belge No</th>
                    <th className="text-right p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredLogs?.map(log => {
                    const statusCfg = STATUS_CONFIG?.[log?.status] || STATUS_CONFIG?.skipped;
                    const isExpanded = expandedLog === log?.id;
                    return (
                      <React.Fragment key={log?.id}>
                        <tr className="hover:bg-muted/30 transition-colors">
                          <td className="p-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${statusCfg?.className}`}>
                              <Icon name={statusCfg?.icon} size={12} />
                              {statusCfg?.label}
                            </span>
                            {log?.is_retry && (
                              <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary border border-primary/20">
                                <Icon name="RotateCcw" size={10} />
                                Tekrar
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="text-sm font-mono text-foreground">
                              {log?.order_number || <span className="text-muted-foreground">—</span>}
                            </span>
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="text-sm text-foreground font-medium truncate max-w-[160px]">
                                {log?.customer_name || <span className="text-muted-foreground italic">—</span>}
                              </p>
                              {log?.customer_vkn_tckn && (
                                <p className="text-xs text-muted-foreground font-mono">{log?.customer_vkn_tckn}</p>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="text-sm text-foreground font-medium">
                              {log?.total_amount != null
                                ? `₺${parseFloat(log?.total_amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                : <span className="text-muted-foreground">—</span>}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-muted-foreground">
                              {SOURCE_LABELS?.[log?.source] || log?.source || '—'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(log?.created_at)}
                            </span>
                          </td>
                          <td className="p-3 max-w-[200px]">
                            {log?.status === 'success' ? (
                              <div>
                                {log?.doc_no && (
                                  <p className="text-xs font-mono text-success">Belge: {log?.doc_no}</p>
                                )}
                                {log?.ettn && (
                                  <p className="text-xs font-mono text-muted-foreground truncate">{log?.ettn}</p>
                                )}
                              </div>
                            ) : log?.status === 'error' ? (
                              <div>
                                <p className="text-xs text-error truncate" title={log?.error_message}>
                                  {log?.error_message || 'Bilinmeyen hata'}
                                </p>
                                {log?.error_stage && (
                                  <p className="text-xs text-muted-foreground">Aşama: {log?.error_stage}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">
                                {log?.error_message || 'Atlandı'}
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => setExpandedLog(isExpanded ? null : log?.id)}
                                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                title="Detayları göster"
                              >
                                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
                              </button>
                              {log?.status !== 'success' && log?.order_id && (
                                <button
                                  onClick={() => handleResend(log)}
                                  disabled={resendingId === log?.id}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Yeniden gönder"
                                >
                                  {resendingId === log?.id ? (
                                    <Icon name="Loader2" size={12} className="animate-spin" />
                                  ) : (
                                    <Icon name="Send" size={12} />
                                  )}
                                  {resendingId === log?.id ? 'Gönderiliyor...' : 'Tekrar Gönder'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Detail Row */}
                        {isExpanded && (
                          <tr className="bg-muted/20">
                            <td colSpan={8} className="px-4 py-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                                <div>
                                  <p className="text-muted-foreground font-medium mb-1">Log ID</p>
                                  <p className="font-mono text-foreground break-all">{log?.id}</p>
                                </div>
                                {log?.order_id && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">Sipariş ID</p>
                                    <p className="font-mono text-foreground break-all">{log?.order_id}</p>
                                  </div>
                                )}
                                {log?.customer_email && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">Müşteri E-posta</p>
                                    <p className="text-foreground">{log?.customer_email}</p>
                                  </div>
                                )}
                                {log?.e_document_type && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">Belge Türü</p>
                                    <p className="text-foreground">{log?.e_document_type}</p>
                                  </div>
                                )}
                                {log?.invoice_type && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">Fatura Türü</p>
                                    <p className="text-foreground">{log?.invoice_type}</p>
                                  </div>
                                )}
                                {log?.retry_count > 0 && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">Tekrar Sayısı</p>
                                    <p className="text-foreground">{log?.retry_count}</p>
                                  </div>
                                )}
                                {log?.last_retry_at && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">Son Tekrar</p>
                                    <p className="text-foreground">{formatDate(log?.last_retry_at)}</p>
                                  </div>
                                )}
                                {log?.sent_at && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">Gönderim Tarihi</p>
                                    <p className="text-foreground">{formatDate(log?.sent_at)}</p>
                                  </div>
                                )}
                                {log?.pdf_url && (
                                  <div>
                                    <p className="text-muted-foreground font-medium mb-1">PDF</p>
                                    <a
                                      href={log?.pdf_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline inline-flex items-center gap-1"
                                    >
                                      <Icon name="FileText" size={12} />
                                      PDF İndir
                                    </a>
                                  </div>
                                )}
                                {log?.error_message && log?.status === 'error' && (
                                  <div className="sm:col-span-2 lg:col-span-3">
                                    <p className="text-muted-foreground font-medium mb-1">Hata Detayı</p>
                                    <p className="text-error bg-error/5 rounded p-2 break-words">{log?.error_message}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-border">
              {filteredLogs?.map(log => {
                const statusCfg = STATUS_CONFIG?.[log?.status] || STATUS_CONFIG?.skipped;
                return (
                  <div key={log?.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${statusCfg?.className}`}>
                          <Icon name={statusCfg?.icon} size={12} />
                          {statusCfg?.label}
                        </span>
                        {log?.is_retry && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary border border-primary/20">
                            <Icon name="RotateCcw" size={10} />
                            Tekrar
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(log?.created_at)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Sipariş No</p>
                        <p className="font-mono font-medium text-foreground">{log?.order_number || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Tutar</p>
                        <p className="font-medium text-foreground">
                          {log?.total_amount != null
                            ? `₺${parseFloat(log?.total_amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                            : '—'}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Müşteri</p>
                        <p className="font-medium text-foreground">{log?.customer_name || '—'}</p>
                        {log?.customer_vkn_tckn && (
                          <p className="text-xs text-muted-foreground font-mono">{log?.customer_vkn_tckn}</p>
                        )}
                      </div>
                    </div>
                    {log?.status === 'error' && log?.error_message && (
                      <div className="bg-error/5 rounded-lg p-2 border border-error/10">
                        <p className="text-xs text-error">{log?.error_message}</p>
                      </div>
                    )}
                    {log?.status === 'success' && log?.doc_no && (
                      <div className="bg-success/5 rounded-lg p-2 border border-success/10">
                        <p className="text-xs text-success">Belge No: {log?.doc_no}</p>
                      </div>
                    )}
                    {log?.status !== 'success' && log?.order_id && (
                      <button
                        onClick={() => handleResend(log)}
                        disabled={resendingId === log?.id}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-colors disabled:opacity-50"
                      >
                        {resendingId === log?.id ? (
                          <Icon name="Loader2" size={14} className="animate-spin" />
                        ) : (
                          <Icon name="Send" size={14} />
                        )}
                        {resendingId === log?.id ? 'Gönderiliyor...' : 'Tekrar Gönder'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                {filteredLogs?.length} kayıt gösteriliyor
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="text-sm text-foreground font-medium px-2">Sayfa {page + 1}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={filteredLogs?.length < PAGE_SIZE || loading}
                  className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MysoftSyncLogs;
